import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { Button } from '../Button';
import { ArrowRight, CheckCircle2, Star, Palette, Building, HardHat } from 'lucide-react';
import ReactGA from 'react-ga4';

const images = [
  "https://images.unsplash.com/photo-1600210492486-724fe5c67fb0",
  "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d",
  "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6"
];

export function HeroSection() {
  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % images.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section aria-label="Hero" className="relative w-full h-[100vh] min-h-[700px] flex items-center pt-20 overflow-hidden bg-primary">
      {/* Background Slider */}
      <div className="absolute inset-0 z-0">
        {images.map((src, index) => (
          <motion.div
            key={src}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{
              opacity: currentImage === index ? 1 : 0,
              scale: currentImage === index ? 1 : 1.05
            }}
            transition={{ duration: 1.5, ease: "easeInOut" }}
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{ zIndex: currentImage === index ? 1 : 0 }}
          >
            <img
              src={`${src}?auto=format&fit=crop&q=85&w=1600`}
              srcSet={`
                ${src}?auto=format&fit=crop&q=85&w=640&fm=webp 640w,
                ${src}?auto=format&fit=crop&q=85&w=1024&fm=webp 1024w,
                ${src}?auto=format&fit=crop&q=85&w=1600&fm=webp 1600w,
                ${src}?auto=format&fit=crop&q=85&w=2000&fm=webp 2000w
              `}
              sizes="100vw"
              alt={`Premium interior design by Aura Spaces Hyderabad - showcase ${index + 1}`}
              fetchPriority={index === 0 ? "high" : "low"}
              loading={index === 0 ? "eager" : "lazy"}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </motion.div>
        ))}

        {/* Layered Gradient Overlay for better contrast while keeping image visible */}
        <div className="absolute inset-0 bg-black/40 sm:bg-transparent z-10" />
        <div className="absolute inset-0 hidden sm:block bg-linear-to-r from-primary/95 via-primary/80 to-transparent z-10" />
        <div className="absolute inset-0 bg-linear-to-t from-primary/80 via-transparent to-primary/20 z-10" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20 w-full">
        <div className="flex items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="w-full max-w-2xl lg:max-w-4xl xl:max-w-5xl"
          >
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6 sm:mb-8">
                <Star className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-accent fill-accent" />
                <span className="text-white text-xs sm:text-sm font-medium tracking-wide">Award-Winning Interior Design Studio</span>
              </div>

              <h1 className="text-[2.5rem] sm:text-6xl lg:text-7xl font-poppins font-bold text-white leading-[1.1] mb-4 sm:mb-6 text-balance">
                Spaces That <br className="hidden md:block" />
                <span className="text-accent italic font-light">Reflect</span> Who <br className="hidden md:block" />
                You Are.
              </h1>

              <p className="text-base sm:text-xl text-gray-200 mb-8 sm:mb-10 max-w-xl font-light leading-relaxed">
                Aura Spaces transforms empty rooms into breathtaking living experiences. End-to-end interior design, flawless execution, zero compromises.
              </p>

              <div className="flex flex-wrap gap-3 sm:gap-4 mb-10 sm:mb-12">
                <Button variant="accent" className="group text-sm sm:text-base py-3 px-6 sm:px-8 sm:py-4 shadow-xl shadow-accent/20" onClick={() => { ReactGA.event({ category: 'CTA', action: 'Clicked Start Your Project' }); document.getElementById('consultation')?.scrollIntoView({ behavior: 'smooth' }); }}>
                  Start Your Project
                  <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button variant="glass" className="text-sm sm:text-base py-3 px-6 sm:px-8 sm:py-4" onClick={() => { ReactGA.event({ category: 'CTA', action: 'Clicked Explore Portfolio' }); document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' }); }}>
                  Explore Portfolio
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 items-start gap-6 lg:gap-8 text-sm text-white/80 border-t border-white/20 pt-6 sm:pt-8 w-full">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 shrink-0 rounded-full bg-white/10 flex items-center justify-center">
                  <Palette className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <div className="font-bold text-white text-base">35+</div>
                  <div className="text-[10px] sm:text-xs uppercase tracking-wider opacity-80">Interior Design <br className="hidden lg:block xl:hidden"/>& Execution</div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 shrink-0 rounded-full bg-white/10 flex items-center justify-center">
                  <Building className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <div className="font-bold text-white text-base">50+</div>
                  <div className="text-[10px] sm:text-xs uppercase tracking-wider opacity-80">Architecture</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 shrink-0 rounded-full bg-white/10 flex items-center justify-center">
                  <HardHat className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <div className="font-bold text-white text-base">15+</div>
                  <div className="text-[10px] sm:text-xs uppercase tracking-wider opacity-80">Construction</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 shrink-0 rounded-full bg-white/10 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <div className="font-bold text-white text-base">70+</div>
                  <div className="text-[10px] sm:text-xs uppercase tracking-wider opacity-80">Projects Done</div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Decorative Elements on Right Side (hidden on mobile) */}

        </div>
      </div>
    </section>
  );
}
